/**
 * The pure client-side mirrors of SAL-03's arithmetic — the server is the
 * authority (the cumulative BR-SAL-03-02 cap re-derives at posting); these
 * keep the editor honest between keystrokes.
 *
 * The REMAINING figure itself is not mirrored here any more: the server
 * computes `remaining` (ordered − Σ earlier posted notes) on every line it
 * returns, and a client copy would have had to guess at the same ledger
 * from figures it does not hold. The helper that did so was never called.
 */

/**
 * The Backorder chip + amber row (BR-SAL-03-05): delivering short of what
 * was LEFT to deliver — `remaining`, the server's ordered − already posted —
 * never short of the full order quantity, or the second of two notes that
 * together complete an order wears the chip for goods the first already
 * shipped.
 */
export function isShortDelivery(qty: string, remaining: string): boolean {
  const qtyNum = Number(qty);
  const remainingNum = Number(remaining);
  if (Number.isNaN(qtyNum) || Number.isNaN(remainingNum)) return false;
  return qtyNum < remainingNum;
}

/**
 * Wave 5B — an ISO instant from the server ("…Z") as the value a
 * `datetime-local` box takes: the reader's LOCAL wall-clock, to the minute.
 * '' when there is nothing to show. The box is the only place the local
 * zone enters: everything stored is the instant.
 */
export function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const two = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}T${two(at.getHours())}:${two(at.getMinutes())}`;
}

/**
 * The inverse: what the `datetime-local` box holds (local wall-clock) as the
 * ISO instant the wire carries; undefined when the box is empty or not a
 * time at all, so a caller spreads it in only when there is one.
 */
export function fromLocalDateTimeInput(local: string): string | undefined {
  if (local.trim() === '') return undefined;
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/**
 * BR-SAL-02-03's fulfillment figure mirrored: Σ delivered ÷ Σ ordered, as a
 * whole-number percent for the chip (the server stores the exact 2-dp one).
 */
export function fulfillmentPct(
  lines: readonly { qty: string; ordered: string }[],
): number {
  let ordered = 0;
  let delivered = 0;
  for (const line of lines) {
    const orderedNum = Number(line.ordered);
    const qtyNum = Number(line.qty);
    if (!Number.isNaN(orderedNum)) ordered += orderedNum;
    if (!Number.isNaN(qtyNum)) delivered += qtyNum;
  }
  if (ordered <= 0) return 0;
  return Math.min(Math.round((delivered / ordered) * 100), 100);
}
